import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const CATEGORIES = [
  { key: 'attorney',         label: 'Attorney',           icon: '⚖️' },
  { key: 'accountant',       label: 'Accountant',         icon: '🧾' },
  { key: 'financial_advisor',label: 'Financial Advisor',  icon: '📈' },
  { key: 'doctor',           label: 'Doctor',             icon: '🩺' },
  { key: 'executor',         label: 'Executor',           icon: '📋' },
  { key: 'other',            label: 'Other',              icon: '👤' },
];

const EMPTY_FORM = { category: 'attorney', full_name: '', company: '', phone: '', email: '', address: '', notes: '' };

export default function Contacts({ session }) {
  const [vault, setVault] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [session]);

  const load = async () => {
    const { data: vaultData } = await supabase
      .from('vaults').select('id').eq('owner_id', session.user.id).single();
    setVault(vaultData);
    if (vaultData) {
      const { data } = await supabase
        .from('contacts').select('*')
        .eq('vault_id', vaultData.id)
        .order('category').order('full_name');
      setContacts(data || []);
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSaving(true);
    if (editing) {
      const { error: err } = await supabase.from('contacts')
        .update({ ...form, updated_at: new Date().toISOString() })
        .eq('id', editing.id);
      if (err) { setError(err.message); setSaving(false); return; }
      setSuccess(`${form.full_name} updated.`);
    } else {
      const { error: err } = await supabase.from('contacts')
        .insert({ ...form, vault_id: vault.id, owner_id: session.user.id });
      if (err) { setError(err.message); setSaving(false); return; }
      setSuccess(`${form.full_name} added.`);
    }
    setSaving(false);
    setShowForm(false); setEditing(null); setForm(EMPTY_FORM);
    setTimeout(() => setSuccess(''), 3000);
    load();
  };

  const handleEdit = (contact) => {
    setEditing(contact);
    setForm({
      category: contact.category, full_name: contact.full_name,
      company: contact.company || '', phone: contact.phone || '',
      email: contact.email || '', address: contact.address || '',
      notes: contact.notes || '',
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (contact) => {
    if (!window.confirm(`Remove ${contact.full_name}?`)) return;
    setDeleting(contact.id);
    await supabase.from('contacts').delete().eq('id', contact.id);
    setDeleting(null);
    load();
  };

  const handleCancel = () => {
    setShowForm(false); setEditing(null); setForm(EMPTY_FORM); setError('');
  };

  const style = `
    .ct { padding: 32px; max-width: 860px; margin: 0 auto; }

    .ct-header { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 28px; flex-wrap: wrap; gap: 16px; }
    .ct-title { font-family: 'Fraunces', serif; font-weight: 900; font-size: 32px; color: #1E1040; line-height: 1.05; }
    .ct-title em { font-style: italic; color: #8B5CF6; }
    .ct-sub { font-size: 14px; color: #6B7280; margin-top: 6px; line-height: 1.5; }
    .ct-add-btn {
      display: flex; align-items: center; gap: 8px;
      background: linear-gradient(135deg, #8B5CF6, #38BDF8);
      color: #fff; border: none; border-radius: 100px;
      padding: 12px 24px; font-family: 'Plus Jakarta Sans', sans-serif;
      font-weight: 700; font-size: 14px; cursor: pointer; white-space: nowrap;
      box-shadow: 0 4px 12px rgba(139,92,246,0.25);
      transition: opacity 0.2s, transform 0.2s;
    }
    .ct-add-btn:hover { opacity: 0.88; transform: translateY(-1px); }

    /* ALERTS */
    .ct-error { background: rgba(239,68,68,0.06); border: 1px solid rgba(239,68,68,0.2); border-radius: 10px; padding: 12px 16px; font-size: 13px; color: #DC2626; margin-bottom: 16px; }
    .ct-success { background: rgba(34,197,94,0.06); border: 1px solid rgba(34,197,94,0.2); border-radius: 10px; padding: 12px 16px; font-size: 13px; color: #16A34A; margin-bottom: 16px; }

    /* FORM */
    .ct-form {
      background: #fff; border: 1.5px solid #EAE5F5; border-radius: 20px;
      padding: 28px; margin-bottom: 28px;
      box-shadow: 0 4px 20px rgba(139,92,246,0.07);
    }
    .ct-form-title { font-family: 'Fraunces', serif; font-weight: 900; font-size: 20px; color: #1E1040; margin-bottom: 20px; }

    /* CATEGORY SELECTOR */
    .ct-cat-grid { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 20px; }
    .ct-cat-btn {
      display: flex; align-items: center; gap: 6px;
      padding: 8px 16px; border-radius: 100px;
      border: 1.5px solid #EAE5F5; background: #FDFCFF;
      font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13px; font-weight: 500;
      color: #6B7280; cursor: pointer; transition: all 0.15s;
    }
    .ct-cat-btn:hover { border-color: rgba(139,92,246,0.3); color: #7C3AED; }
    .ct-cat-btn.selected { border-color: #8B5CF6; background: rgba(139,92,246,0.06); color: #7C3AED; font-weight: 700; }

    .ct-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-bottom: 14px; }
    @media(max-width:600px){ .ct-form-grid { grid-template-columns: 1fr; } }
    .ct-field { display: flex; flex-direction: column; gap: 6px; }
    .ct-field.full { grid-column: 1 / -1; }
    .ct-label { font-size: 12px; font-weight: 700; color: #374151; }
    .ct-input, .ct-textarea {
      padding: 11px 14px; border: 1.5px solid #E5E7EB; border-radius: 10px;
      font-family: 'Plus Jakarta Sans', sans-serif; font-size: 14px; color: #1E1040;
      background: #FAFAFA; outline: none; transition: border-color 0.2s; width: 100%;
    }
    .ct-input:focus, .ct-textarea:focus { border-color: #8B5CF6; background: #fff; }
    .ct-textarea { resize: vertical; min-height: 80px; }
    .ct-form-actions { display: flex; gap: 10px; justify-content: flex-end; margin-top: 4px; }
    .ct-btn-cancel {
      padding: 10px 20px; border: 1.5px solid #EDE9E3; border-radius: 100px;
      background: transparent; color: #6B7280;
      font-family: 'Plus Jakarta Sans', sans-serif; font-size: 14px; font-weight: 600;
      cursor: pointer; transition: all 0.15s;
    }
    .ct-btn-cancel:hover { border-color: #D1D5DB; }
    .ct-btn-save {
      padding: 10px 24px; border: none; border-radius: 100px;
      background: linear-gradient(135deg, #8B5CF6, #38BDF8); color: #fff;
      font-family: 'Plus Jakarta Sans', sans-serif; font-size: 14px; font-weight: 700;
      cursor: pointer; box-shadow: 0 4px 12px rgba(139,92,246,0.25);
      transition: opacity 0.2s;
    }
    .ct-btn-save:hover { opacity: 0.88; }
    .ct-btn-save:disabled { opacity: 0.6; cursor: not-allowed; }

    /* CONTACT GROUPS */
    .ct-group { margin-bottom: 28px; }
    .ct-group-header {
      display: flex; align-items: center; gap: 10px;
      margin-bottom: 12px; padding-bottom: 10px;
      border-bottom: 1px solid #EAE5F5;
    }
    .ct-group-icon { font-size: 18px; }
    .ct-group-label { font-size: 13px; font-weight: 700; color: #374151; letter-spacing: 0.04em; text-transform: uppercase; }
    .ct-group-count { font-size: 12px; color: #9CA3AF; margin-left: auto; }

    /* CONTACT CARD */
    .ct-card {
      background: linear-gradient(145deg, #FDFCFF, #FAF8FF);
      border: 1.5px solid #EAE5F5; border-radius: 14px; padding: 20px;
      margin-bottom: 10px; display: flex; align-items: flex-start; gap: 16px;
      transition: border-color 0.2s, box-shadow 0.2s;
    }
    .ct-card:hover { border-color: rgba(139,92,246,0.25); box-shadow: 0 4px 16px rgba(139,92,246,0.08); }
    .ct-card-avatar {
      width: 44px; height: 44px; border-radius: 12px; flex-shrink: 0;
      background: linear-gradient(135deg, #8B5CF6, #38BDF8);
      display: flex; align-items: center; justify-content: center;
      font-size: 18px; font-weight: 800; color: #fff;
    }
    .ct-card-info { flex: 1; min-width: 0; }
    .ct-card-name { font-size: 16px; font-weight: 700; color: #1E1040; margin-bottom: 2px; }
    .ct-card-company { font-size: 13px; color: #6B7280; margin-bottom: 8px; }
    .ct-card-details { display: flex; flex-wrap: wrap; gap: 16px; }
    .ct-card-detail {
      display: flex; align-items: center; gap: 6px;
      font-size: 13px; color: #374151;
    }
    .ct-card-detail-icon { font-size: 14px; color: #8B5CF6; }
    .ct-card-notes {
      margin-top: 10px; padding-top: 10px; border-top: 1px solid #EAE5F5;
      font-size: 13px; color: #6B7280; line-height: 1.5;
    }
    .ct-card-actions { display: flex; flex-direction: column; gap: 6px; flex-shrink: 0; }
    .ct-btn-edit {
      padding: 6px 14px; border-radius: 100px;
      border: 1.5px solid #EAE5F5; background: transparent;
      font-family: 'Plus Jakarta Sans', sans-serif; font-size: 12px; font-weight: 600;
      color: #6B7280; cursor: pointer; transition: all 0.15s;
    }
    .ct-btn-edit:hover { border-color: #8B5CF6; color: #7C3AED; }
    .ct-btn-delete {
      padding: 6px 14px; border-radius: 100px;
      border: 1.5px solid transparent; background: transparent;
      font-family: 'Plus Jakarta Sans', sans-serif; font-size: 12px; font-weight: 600;
      color: #9CA3AF; cursor: pointer; transition: all 0.15s;
    }
    .ct-btn-delete:hover { border-color: rgba(239,68,68,0.2); color: #EF4444; background: rgba(239,68,68,0.04); }
    .ct-btn-delete:disabled { opacity: 0.4; cursor: not-allowed; }

    /* EMPTY STATE */
    .ct-empty {
      background: #fff; border: 2px dashed #EAE5F5; border-radius: 20px;
      padding: 60px 40px; text-align: center;
    }
    .ct-empty-icon { font-size: 48px; margin-bottom: 16px; }
    .ct-empty-title { font-family: 'Fraunces', serif; font-weight: 900; font-size: 22px; color: #1E1040; margin-bottom: 8px; }
    .ct-empty-sub { font-size: 14px; color: #6B7280; line-height: 1.6; max-width: 400px; margin: 0 auto 24px; }

    /* WHY THIS MATTERS */
    .ct-why {
      background: linear-gradient(135deg, rgba(139,92,246,0.05), rgba(56,189,248,0.03));
      border: 1px solid rgba(139,92,246,0.12); border-radius: 16px;
      padding: 20px 24px; margin-bottom: 28px;
      display: flex; gap: 14px; align-items: flex-start;
    }
    .ct-why-icon { font-size: 24px; flex-shrink: 0; margin-top: 2px; }
    .ct-why-text { font-size: 14px; color: #374151; line-height: 1.6; }
    .ct-why-text strong { color: #1E1040; }
  `;

  if (loading) return <div style={{padding: 32, color: '#6B7280', fontSize: 14}}>Loading...</div>;

  const initials = (name) => name.split(' ').map(n => n[0]?.toUpperCase()).filter(Boolean).slice(0,2).join('');

  return (
    <>
      <style>{style}</style>
      <div className="ct">

        <div className="ct-header">
          <div>
            <h1 className="ct-title">Key <em>Contacts</em></h1>
            <p className="ct-sub">The people your family will need to call. Store them here so they're never lost.</p>
          </div>
          {!showForm && (
            <button className="ct-add-btn" onClick={() => setShowForm(true)}>
              + Add Contact
            </button>
          )}
        </div>

        {/* WHY THIS MATTERS */}
        {contacts.length === 0 && !showForm && (
          <div className="ct-why">
            <div className="ct-why-icon">💡</div>
            <div className="ct-why-text">
              <strong>Why this matters:</strong> When something happens, your family won't know who to call. Your attorney, accountant, doctor, and financial advisor are the first people they'll need — and without this, finding them can take weeks.
            </div>
          </div>
        )}

        {error && <div className="ct-error">{error}</div>}
        {success && <div className="ct-success">✓ {success}</div>}

        {/* ADD / EDIT FORM */}
        {showForm && (
          <form className="ct-form" onSubmit={handleSubmit}>
            <div className="ct-form-title">{editing ? 'Edit Contact' : 'Add a Key Contact'}</div>

            {/* Category selector */}
            <div className="ct-cat-grid">
              {CATEGORIES.map(cat => (
                <button key={cat.key} type="button"
                  className={`ct-cat-btn${form.category === cat.key ? ' selected' : ''}`}
                  onClick={() => setForm({...form, category: cat.key})}>
                  {cat.icon} {cat.label}
                </button>
              ))}
            </div>

            <div className="ct-form-grid">
              <div className="ct-field">
                <label className="ct-label">Full Name *</label>
                <input className="ct-input" type="text" placeholder="Jane Smith"
                  value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})} required />
              </div>
              <div className="ct-field">
                <label className="ct-label">Company / Practice</label>
                <input className="ct-input" type="text" placeholder="Smith & Associates"
                  value={form.company} onChange={e => setForm({...form, company: e.target.value})} />
              </div>
              <div className="ct-field">
                <label className="ct-label">Phone</label>
                <input className="ct-input" type="tel" placeholder="(555) 123-4567"
                  value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} />
              </div>
              <div className="ct-field">
                <label className="ct-label">Email</label>
                <input className="ct-input" type="email" placeholder="jane@smithlaw.com"
                  value={form.email} onChange={e => setForm({...form, email: e.target.value})} />
              </div>
              <div className="ct-field full">
                <label className="ct-label">Address</label>
                <input className="ct-input" type="text" placeholder="123 Main St, Dallas, TX 75201"
                  value={form.address} onChange={e => setForm({...form, address: e.target.value})} />
              </div>
              <div className="ct-field full">
                <label className="ct-label">Notes</label>
                <textarea className="ct-textarea" placeholder="Any important details — office hours, account numbers, relationship context..."
                  value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} />
              </div>
            </div>

            <div className="ct-form-actions">
              <button type="button" className="ct-btn-cancel" onClick={handleCancel}>Cancel</button>
              <button type="submit" className="ct-btn-save" disabled={saving}>
                {saving ? 'Saving...' : editing ? 'Save Changes' : 'Add Contact'}
              </button>
            </div>
          </form>
        )}

        {/* CONTACT LIST */}
        {contacts.length === 0 && !showForm ? (
          <div className="ct-empty">
            <div className="ct-empty-icon">👥</div>
            <div className="ct-empty-title">No contacts yet</div>
            <p className="ct-empty-sub">Add your attorney, doctor, accountant, and other key people your family will need to reach.</p>
            <button className="ct-add-btn" onClick={() => setShowForm(true)}>+ Add Your First Contact</button>
          </div>
        ) : (
          CATEGORIES.map(cat => {
            const items = contacts.filter(c => c.category === cat.key);
            if (items.length === 0) return null;
            return (
              <div key={cat.key} className="ct-group">
                <div className="ct-group-header">
                  <span className="ct-group-icon">{cat.icon}</span>
                  <span className="ct-group-label">{cat.label}</span>
                  <span className="ct-group-count">{items.length}</span>
                </div>
                {items.map(contact => (
                  <div key={contact.id} className="ct-card">
                    <div className="ct-card-avatar">{initials(contact.full_name)}</div>
                    <div className="ct-card-info">
                      <div className="ct-card-name">{contact.full_name}</div>
                      {contact.company && <div className="ct-card-company">{contact.company}</div>}
                      <div className="ct-card-details">
                        {contact.phone && (
                          <div className="ct-card-detail">
                            <span className="ct-card-detail-icon">📞</span>
                            <a href={`tel:${contact.phone}`} style={{color:'inherit',textDecoration:'none'}}>{contact.phone}</a>
                          </div>
                        )}
                        {contact.email && (
                          <div className="ct-card-detail">
                            <span className="ct-card-detail-icon">✉️</span>
                            <a href={`mailto:${contact.email}`} style={{color:'inherit',textDecoration:'none'}}>{contact.email}</a>
                          </div>
                        )}
                        {contact.address && (
                          <div className="ct-card-detail">
                            <span className="ct-card-detail-icon">📍</span>
                            {contact.address}
                          </div>
                        )}
                      </div>
                      {contact.notes && <div className="ct-card-notes">{contact.notes}</div>}
                    </div>
                    <div className="ct-card-actions">
                      <button className="ct-btn-edit" onClick={() => handleEdit(contact)}>Edit</button>
                      <button className="ct-btn-delete" disabled={deleting === contact.id}
                        onClick={() => handleDelete(contact)}>
                        {deleting === contact.id ? '...' : 'Remove'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            );
          })
        )}
      </div>
    </>
  );
}
